<template>
  <section>
    <div :class="$style.list">
      <template v-for="(product, index) in productList" :key="product?.document_id">
        <AtomsConsumerLink v-if="product" :url="getProductLink(product)" :class="$style.list_item">
          <div :class="$style.image_box">
            <div v-if="isDisplayLabels(product)" :class="$style.promotion_labels">
              <!-- 在庫切れ、交換対象商品、販促ラベルの表示 -->
              <ProductLabelBasic
                :is-out-of-stock="isAllOutOfStock(product)"
                :is-general-medical-device="false"
                :is-quasi-drug="false"
                :is-duvet-collection="!!product.is_duvet_collection"
                :promotion-labels="product.promotion_labels"
              />
            </div>
            <AtomsConsumerImage
              :src="`${product.skus[0].main_image}`"
              :alt="`${product.name} - 商品画像`"
              :width="184 * 2"
              :height="245 * 2"
              :class="$style.img"
            />
          </div>
          <div :class="$style.product_box">
            <div v-if="!isHideRankingNum" :class="$style.ranking_num">
              <p>No.{{ index + 1 }}</p>
            </div>
            <!-- 一般医療機器か医薬部外品か -->
            <ProductLabelBasic
              :is-out-of-stock="false"
              :is-general-medical-device="!!product.is_general_medical_device"
              :is-quasi-drug="!!product.is_quasi_drug"
              :is-duvet-collection="false"
              :promotion-labels="[]"
            />
            <p :class="$style.title">{{ product.name }}</p>
            <p :class="$style.sub_title">{{ product.material }}</p>
            <p :class="$style.price">
              <span :class="$style.yen">
                &yen;
                {{ calcPrice(product.skus[0]).effective_reference_price_comma }}
              </span>
              <span :class="$style.tax">(参考税込)</span>
            </p>
          </div>
        </AtomsConsumerLink>
      </template>
    </div>
    <AtomsConsumerLink v-if="products && products.length > 0 && linkUrl" :url="linkUrl" :class="$style.link">
      <MoleculesConsumerButtonWithArrowRight variant="secondary">
        ランキングをもっと見る
      </MoleculesConsumerButtonWithArrowRight>
    </AtomsConsumerLink>
  </section>
</template>

<script setup lang="ts">
import { calcPrice } from "@tential/ec-gql-schema/utils/functions/price"
import { getSiteProductPageUrl, isAllOutOfStock, isDisplayLabels } from "~/utils/functions/product"
import { PagePartsProductType } from "~/types/product"

type Props = {
  productIds: string[]
  products: PagePartsProductType[]
  linkUrl?: string
  isHideRankingNum: boolean
}

const props = withDefaults(defineProps<Props>(), {
  productIds: () => [],
  products: () => [],
  linkUrl: "",
  isHideRankingNum: false,
})

const { products } = toRefs(props)

const productList = computed(() => {
  const productList = props.productIds.map((id) => products.value.find((p) => p.document_id === id))
  return productList
})

const getProductLink = (product: PagePartsProductType): string => {
  return getSiteProductPageUrl(product)
}
</script>

<style scoped module lang="scss">
.list {
  display: grid;
  grid-template-rows: repeat(3, 1fr);
  grid-template-columns: calc(50% - 1rem) 50%;
  grid-auto-flow: column;
  grid-row-gap: 1rem;
  grid-column-gap: 1rem;
  width: 60%;
  min-width: 620px;
  max-width: 710px;
  margin: 0 auto;

  @include sp {
    display: block;
    width: 85%;
    min-width: unset;
    max-width: 400px;
  }
  .list_item {
    display: flex;
    gap: 0.75rem;
    cursor: pointer;
    @include sp {
      margin-bottom: 6px;
    }
    .image_box {
      position: relative;
      width: 45%;
      .promotion_labels {
        position: absolute;
        top: 0.5rem;
        padding-left: 0.5rem;
      }
      .img {
        aspect-ratio: 184 / 245;
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 0.5rem;
        @include background-skeleton-loading;
      }
    }
    .product_box {
      width: calc(55% - 0.75rem);
      .ranking_num {
        width: 67px;
        padding: 4px 0;
        margin-bottom: 0.625rem;
        font-weight: 700;
        line-height: 19px;
        color: $white;
        text-align: center;
        background-color: #657380;
        @include sp {
          width: 60px;
          padding: 0;
          margin-bottom: 4px;
          font-size: $font-size-14;
          line-height: 21px;
        }
      }
      .medical_device_tag,
      .quasi_drug_tag {
        padding: 0.125rem 0.5rem;
        line-height: 0.6875rem;
        background-color: $secondary;
        color: $white;
        font-size: $font-size-10;
        text-wrap: nowrap;

        @include lg {
          font-size: $font-size-12;
        }
      }
      .title {
        @include main-font;
        color: $primary;
        font-size: $font-size-14;
        line-height: 1.25rem;
        margin: 0.25rem 0;
      }
      .sub_title {
        font-size: $font-size-13;
        margin-bottom: 0.5rem;
        font-weight: normal;
      }
      .price {
        color: $primary;
        .yen {
          @include main-font;
          font-size: $font-size-14;
          font-weight: 700;
        }
        .tax {
          font-size: $font-size-10;
          font-weight: 300;
          line-height: 14px 3vw;
          margin-left: 0.25rem;
        }
      }
    }

    &:nth-child(1) {
      display: block;
      grid-row: 1 / 4;

      @include sp {
        margin-bottom: 0.8125rem;
      }
      .image_box {
        position: relative;
        width: 100%;
        margin-bottom: 0.625rem;

        @include sp {
          margin-bottom: 7px;
        }

        &::before {
          padding-top: 96.8%;
        }
      }
      .product_box {
        width: 100%;
        .ranking_num {
          width: 85px;
          padding: 7px 0;
          margin-bottom: 5px;
          line-height: 17px;
          background-color: #cb6767;

          @include sp {
            width: 66px;
            padding: 3.5px 0;
            margin-bottom: 6px;
            font-size: $font-size-16;
          }
        }

        .title {
          margin-bottom: 4px;
          line-height: 1.5;

          @include sp {
            font-size: $font-size-15;
            line-height: 20px;
          }
        }

        .price {
          line-height: 2;
          @include sp {
            font-size: $font-size-15;
            line-height: 25px;
          }
        }
      }
    }
  }
}

.link {
  margin-top: 2.5rem;
  display: flex;
  justify-content: center;
}
</style>
